import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { cn } from "../../../lib/utils";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle } from
"../../../components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "../../../components/ui/button";
import { LeaveDateField } from "./LeaveDateField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"../../../components/ui/select";
import {
  DEFAULT_MANAGER_LEAVE_FILTERS,
  areManagerFiltersEqual,
  cloneManagerLeaveFilters } from
"./managerLeaveFilters";

export function ManagerLeaveFiltersModal({
  open,
  onOpenChange,
  appliedFilters,
  onApply,
  employees = [],
  leaveTypes = [],
  employeesLoading = false,
  employeesError = "",
  leaveTypesLoading = false,
  leaveTypesError = ""
}) {
  const [draft, setDraft] = useState(() =>
  cloneManagerLeaveFilters(appliedFilters || DEFAULT_MANAGER_LEAVE_FILTERS)
  );

  useEffect(() => {
    if (open) {
      setDraft(cloneManagerLeaveFilters(appliedFilters || DEFAULT_MANAGER_LEAVE_FILTERS));
    }
  }, [open, appliedFilters]);

  const isUnchanged = useMemo(
    () =>
    areManagerFiltersEqual(draft, appliedFilters || DEFAULT_MANAGER_LEAVE_FILTERS),
    [draft, appliedFilters]
  );

  const updateDraft = (patch) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-xs" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[90vw] md:w-[560px] max-w-[560px]",
            "max-h-[90vh] overflow-hidden flex flex-col",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "font-sans"
          )}>
          
          <div className="flex items-center justify-between px-6 py-3.5 border-b border-(--color-text-border)/70 shrink-0">
            <DialogTitle className="text-[20px] font-semibold text-foreground tracking-tight">
              <Trans i18nKey="formActions.filterLeaveRequests" />
            </DialogTitle>
            <DialogPrimitive.Close className="h-9 w-9 grid place-items-center rounded-xl per-file-hint hover:bg-workspace/60 hover:text-primary transition">
              <X className="h-5 w-5" />
              <span className="sr-only"><Trans i18nKey="formActions.close" /></span>
            </DialogPrimitive.Close>
          </div>

          <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-foreground"><Trans i18nKey="common.employee" /></label>
              <Select
                value={draft.employee}
                onValueChange={(value) => updateDraft({ employee: value })}
                disabled={employeesLoading}>
                
                <SelectTrigger className="h-11 rounded-xl border-(--color-text-border) bg-white text-sm">
                  <SelectValue
                    placeholder={employeesLoading ? "Loading employees..." : "All Employees"} />
                  
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all"><Trans i18nKey="common.allEmployees" /></SelectItem>
                  {employees.map((employee) =>
                  <SelectItem key={employee.id} value={String(employee.id)}>
                      {employee.name}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              {employeesError &&
              <p className="text-xs text-error font-medium">{employeesError}</p>
              }
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-foreground"><Trans i18nKey="leave.leaveType" /></label>
              <Select
                value={draft.leaveType}
                onValueChange={(value) => updateDraft({ leaveType: value })}
                disabled={leaveTypesLoading}>
                
                <SelectTrigger className="h-11 rounded-xl border-(--color-text-border) bg-white text-sm">
                  <SelectValue
                    placeholder={
                    leaveTypesLoading ? "Loading leave types..." : "All Leave Types"
                    } />
                  
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all"><Trans i18nKey="leave.allLeaveTypes" /></SelectItem>
                  {leaveTypes.map((type) =>
                  <SelectItem key={type.typeId} value={String(type.typeId)}>
                      {type.leaveName}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              {leaveTypesError &&
              <p className="text-xs text-error font-medium">
                  {leaveTypesError}
                </p>
              }
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-foreground"><Trans i18nKey="leave.leaveStatus" /></label>
              <Select
                value={draft.status}
                onValueChange={(value) => updateDraft({ status: value })}>
                
                <SelectTrigger className="h-11 rounded-xl border-(--color-text-border) bg-white text-sm">
                  <SelectValue placeholder={i18n.t("common.allStatuses")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all"><Trans i18nKey="common.allStatuses" /></SelectItem>
                  <SelectItem value="pending"><Trans i18nKey="status.pending" /></SelectItem>
                  <SelectItem value="approved"><Trans i18nKey="status.approved" /></SelectItem>
                  <SelectItem value="rejected"><Trans i18nKey="status.rejected" /></SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-foreground"><Trans i18nKey="dateTime.fromDate" /></label>
                <LeaveDateField
                  placeholder={i18n.t("dateTime.fromDate")}
                  value={draft.dateRange?.from}
                  onChange={(from) =>
                  updateDraft({
                    dateRange: { from, to: draft.dateRange?.to }
                  })
                  } />
                
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-foreground"><Trans i18nKey="dateTime.toDate" /></label>
                <LeaveDateField
                  placeholder={i18n.t("dateTime.toDate")}
                  value={draft.dateRange?.to}
                  onChange={(to) =>
                  updateDraft({
                    dateRange: { from: draft.dateRange?.from, to }
                  })
                  } />
                
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-(--color-text-border)/70 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDraft(cloneManagerLeaveFilters(DEFAULT_MANAGER_LEAVE_FILTERS))}
              className="h-11 px-5 rounded-xl border border-(--color-text-border) bg-white text-sm font-semibold per-file-hint hover:text-primary hover:border-primary/40">
              <Trans i18nKey="formActions.clearFilters" />
            
            </Button>
            <Button
              type="button"
              disabled={isUnchanged}
              onClick={() => {
                onApply(cloneManagerLeaveFilters(draft));
                onOpenChange(false);
              }}
              className="h-11 px-5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-accent transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] disabled:opacity-50">
              <Trans i18nKey="formActions.applyFilters" />
            
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>);

}
